// [CurveBeam] - [BoneVue]

export interface RoiInfo {
  /** Width in mm (2px) */
  fnWidth: number;
  /** Height in mm (2px) */
  fnHeight: number;
  fnCentrePt: [number, number];
  // In degrees
  fnAxisAngle: number;
  shaftLine: number;
}

export interface ProjectionTableData {
  roi: string;
  bmd: number;
  tScore: number;
  zScore: number;
}

export interface PatientInfo {
  patientID: string;
  name: string;
  DOB: string;
  age: number;
  sex: 'Male' | 'Female' | 'Other';
  acquisitionDate: string;
  processingDate: string;
  referringPhysicianName: string;
  requestingPhysicianName: string;
  organizationName: string;
}

export interface ResultReport {
  createdDateTime: string;
  downloadUrl: string;
  imageUrls: {
    projectionImageUrl: string;
    femoralNeckUrl: string;
    totalHipUrl: string;
  };
  projectionTableData: ProjectionTableData[];
  patientInfo: PatientInfo;
  laterality: 'Left' | 'Right';
  interpretation: string;
}

export enum ResultStatus {
  New = 'new',
  NoPhantom = 'no_phantom',
  MipProcessing = 'mip_processing',
  MipProcessed = 'mip_processed',
  ErrorMipProcessing = 'error_mip_processing',
  ErrorCropping = 'error_cropping',
  ErrorAutoRotating = 'error_auto_rotating',
  Cropping = 'cropping',
  Cropped = 'cropped',
  ErrorAutoCropping = 'error_auto_cropping',
  Rotating = 'rotating',
  Rotated = 'rotated',
  ErrorRotating = 'error_rotating',
  Segmenting = 'segmenting',
  Segmented = 'segmented',
  ErrorSegmenting = 'error_segmenting',
  RoiProcessing = 'roi_processing',
  RoiProcessed = 'roi_processed',
  ErrorRoiProcessing = 'error_roi_processing',
  BmdProcessing = 'bmd_processing',
  BmdProcessed = 'bmd_processed',
  ErrorBmdProcessing = 'error_bmd_processing',
  Fail = 'fail',
}

export const ResultStatusDisplay: Record<ResultStatus, string> = {
  [ResultStatus.New]: 'New',
  [ResultStatus.NoPhantom]: 'No Phantom',
  [ResultStatus.MipProcessing]: 'In Progress',
  [ResultStatus.MipProcessed]: 'In Progress',
  [ResultStatus.ErrorAutoRotating]: 'Error',
  [ResultStatus.ErrorMipProcessing]: 'Error',
  [ResultStatus.Cropping]: 'In Progress',
  [ResultStatus.Cropped]: 'In Progress',
  [ResultStatus.ErrorCropping]: 'Error',
  [ResultStatus.ErrorAutoCropping]: 'Error',
  [ResultStatus.Rotating]: 'In Progress',
  [ResultStatus.Rotated]: 'In Progress',
  [ResultStatus.ErrorRotating]: 'Error',
  [ResultStatus.Segmenting]: 'In Progress',
  [ResultStatus.Segmented]: 'In Progress',
  [ResultStatus.ErrorSegmenting]: 'Error',
  [ResultStatus.RoiProcessing]: 'In Progress',
  [ResultStatus.RoiProcessed]: 'In Progress',
  [ResultStatus.ErrorRoiProcessing]: 'Error',
  [ResultStatus.BmdProcessing]: 'In Progress',
  [ResultStatus.BmdProcessed]: 'Completed',
  [ResultStatus.ErrorBmdProcessing]: 'Error',
  [ResultStatus.Fail]: 'Error',
};

export const SuccessResultStatuses: ResultStatus[] = [
  ResultStatus.Cropped,
  ResultStatus.Rotated,
  ResultStatus.BmdProcessed,
  ResultStatus.Segmented,
];

export const ErrorResultStatuses: ResultStatus[] = [
  ResultStatus.NoPhantom,
  ResultStatus.ErrorCropping,
  ResultStatus.ErrorRotating,
  ResultStatus.ErrorBmdProcessing,
  ResultStatus.Fail,
  ResultStatus.ErrorAutoRotating,
  ResultStatus.ErrorAutoCropping,
  ResultStatus.ErrorSegmenting,
  ResultStatus.ErrorRoiProcessing,
];

export const InProgressResultStatuses: ResultStatus[] = [
  ResultStatus.New,
  ResultStatus.MipProcessing,
  ResultStatus.MipProcessed,
  ResultStatus.Cropping,
  ResultStatus.Cropped,
  ResultStatus.Rotating,
  ResultStatus.Rotated,
  ResultStatus.Segmenting,
  ResultStatus.Segmented,
  ResultStatus.RoiProcessing,
  ResultStatus.RoiProcessed,
  ResultStatus.BmdProcessing,
];

export enum Workflow {
  Automatic = 'automatic',
  Manual = 'manual',
}

export const WorkflowDisplay: Record<string, string> = {
  [Workflow.Automatic]: 'Automatic',
  [Workflow.Manual]: 'Manual',
};

export interface ResultDetail {
  organizationId: string;
  resultUID: string;
  version: string;
  createdDateTime: string;
  workflow: Workflow;
  status: ResultStatus;
  errorMessage?: string;

  // Only needed when status is cropped
  imageSetId?: string;
  datastoreId?: string;

  // Only needed when status is roi_processed
  roiInfo?: RoiInfo;
  // Final report. Set null when null available
  report?: ResultReport;
  roiProjectionUrl?: string;
}
